import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../services/supabase/supabaseClient';
import { formatK, formatMoney, calculateDelta, calculateMoney, type Currency } from '../../utils/tokenDelta';

interface JobRow {
  id: string;
  user_id: string;
  file_name: string;
  type: string;
  batch_index: number;
  total_batches: number;
  status: string;
  estimated_tokens: number;
  input_tokens: number | null;
  output_tokens: number | null;       // bundled: candidates + thoughts
  thoughts_tokens: number | null;     // thinking portion (subset of output_tokens)
  cost_usd: number | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
}

interface AllJobsTableProps {
  forcedFilter?: 'all' | 'running' | 'error';
}

type RoleFilter = 'all' | 'user' | 'internal';

const AllJobsTable: React.FC<AllJobsTableProps> = ({ forcedFilter }) => {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, { name: string; code: string; role: string }>>({});
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [currency, setCurrency] = useState<Currency>('USD');

  const fetchJobs = useCallback(async () => {
    let query = supabase
      .from('jobs')
      .select('id, user_id, file_name, type, batch_index, total_batches, status, estimated_tokens, input_tokens, output_tokens, thoughts_tokens, cost_usd, created_at, started_at, completed_at, error')
      .order('created_at', { ascending: false })
      .limit(150);

    if (forcedFilter && forcedFilter !== 'all') {
      if (forcedFilter === 'running') {
        query = query.in('status', ['running', 'queued', 'pending']);
      } else if (forcedFilter === 'error') {
        query = query.in('status', ['error', 'stuck']);
      } else {
        query = query.eq('status', forcedFilter);
      }
    }

    const { data } = await query;
    const rows = (data as JobRow[] | null) || [];
    setJobs(rows);

    const userIds = Array.from(new Set(rows.map((j: JobRow) => j.user_id).filter(Boolean)));
    const missing = userIds.filter(id => !profiles[id]);
    if (missing.length > 0) {
      const { data: profs } = await supabase
        .from('profiles')
        .select('id, display_name, employee_code, role')
        .in('id', missing);
      if (profs) {
        setProfiles(prev => {
          const next = { ...prev };
          for (const p of profs) {
            next[p.id] = { name: p.display_name || '', code: p.employee_code || '', role: p.role || 'user' };
          }
          return next;
        });
      }
    }
    setLoading(false);
  }, [forcedFilter, profiles]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 10_000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const statusBadge = (status: string) => {
    const isErr = status === 'error' || status === 'stuck';
    const isRun = status === 'running';
    const isDone = status === 'done';
    const isQueued = status === 'queued';

    let color = '';
    if (isErr) color = 'bg-red-500 text-white';
    else if (isRun) color = 'bg-blue-500 text-white';
    else if (isQueued) color = 'bg-amber-500 text-white';
    else if (isDone) color = 'bg-emerald-500 text-white';
    else color = 'bg-slate-400 text-white'; // pending

    return (
      <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-bold tracking-widest ${color}`}>
        {status}
      </span>
    );
  };

  const formatDate = (ts: string | null) => {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('vi-VN', { month: '2-digit', day: '2-digit' });
  };

  const formatTime = (ts: string | null) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // US-11-05: filter by role (paid commercial vs internal/admin free access)
  const filteredJobs = jobs.filter(j => {
    if (roleFilter === 'all') return true;
    const role = profiles[j.user_id]?.role || 'user';
    if (roleFilter === 'user') return role === 'user';
    if (roleFilter === 'internal') return role === 'internal' || role === 'admin';
    return true;
  });

  // Totals for stats strip — money only counts commercial paid users (role='user')
  // All values USD canonical; display converts to VND via formatMoney(val, currency)
  const totals = filteredJobs.reduce(
    (acc: { realTokens: number; revenueUsd: number; costUsd: number; freeTokens: number }, j: JobRow) => {
      const input = j.input_tokens || 0;
      const output = j.output_tokens || 0;
      const real = input + output;
      const role = profiles[j.user_id]?.role || 'user';
      const money = calculateMoney(real, j.cost_usd);
      acc.realTokens += real;
      acc.costUsd += money.costUsd;  // Gemini cost applies regardless of who (chúng ta vẫn trả Google)
      if (role === 'user') {
        acc.revenueUsd += money.revenueUsd;
      } else {
        acc.freeTokens += real;  // internal/admin tokens (free access)
      }
      return acc;
    },
    { realTokens: 0, revenueUsd: 0, costUsd: 0, freeTokens: 0 }
  );
  const totalProfit = totals.revenueUsd - totals.costUsd;
  const totalMargin = totals.revenueUsd > 0 ? (totalProfit / totals.revenueUsd) * 100 : 0;

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden text-[11px] font-sans">
      {/* Summary strip */}
      <div className="px-3 py-1.5 bg-slate-100 border-b border-slate-300 flex items-center gap-4 text-[10px] font-mono text-slate-600 shrink-0">
        <span>📊 <b>{filteredJobs.length}</b>/{jobs.length} jobs</span>
        <span>💾 <b>{formatK(totals.realTokens)}</b> tokens</span>
        <span>💰 Thu: <b className="text-emerald-700">{formatMoney(totals.revenueUsd, currency)}</b> <span className="text-slate-400">(paid only)</span></span>
        <span>💸 Chi Gemini: <b className="text-red-600">{formatMoney(totals.costUsd, currency)}</b></span>
        <span>📈 Lãi: <b className={totalProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}>{formatMoney(totalProfit, currency)}</b> <span className="text-slate-500">({totalMargin.toFixed(0)}%)</span></span>
        {totals.freeTokens > 0 && (
          <span className="text-violet-600">🎁 Free: <b>{formatK(totals.freeTokens)}</b> <span className="text-slate-400">(internal/admin)</span></span>
        )}
        {/* Currency toggle */}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setCurrency(currency === 'USD' ? 'VND' : 'USD')}
            className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border cursor-pointer transition-colors ${
              currency === 'USD'
                ? 'bg-blue-100 border-blue-300 text-blue-700 hover:bg-blue-200'
                : 'bg-orange-100 border-orange-300 text-orange-700 hover:bg-orange-200'
            }`}
            title={currency === 'USD' ? 'Click đổi sang VND (×25,000)' : 'Click đổi về USD'}
          >
            {currency === 'USD' ? '💵 USD' : '💰 VND'}
          </button>
          {/* Role filter dropdown */}
          <label className="text-slate-500">Role:</label>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
            className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-[10px] font-mono text-slate-700 cursor-pointer"
          >
            <option value="all">Tất cả</option>
            <option value="user">💰 Paid (user)</option>
            <option value="internal">🎁 Free (internal+admin)</option>
          </select>
        </div>
      </div>
      {loading ? (
        <div className="p-2 text-slate-400">Loading DB...</div>
      ) : jobs.length === 0 ? (
        <div className="p-2 text-slate-400">No jobs found in this view.</div>
      ) : (
        <div className="overflow-auto flex-1 bg-white">
          <table className="text-left" style={{ borderCollapse: 'collapse', minWidth: '1800px' }}>
            <thead className="bg-slate-200 sticky top-0 z-10 font-bold border-b-2 border-slate-400 shadow-sm">
              <tr>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-16 text-slate-700 font-normal">Date</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-16 text-slate-700 font-normal">Time</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-16 text-slate-700 font-normal">Status</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-12 text-slate-700 font-normal text-center">Retry</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-[220px] text-slate-700 font-normal">File Name</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-16 text-slate-700 font-normal">Type</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-12 text-slate-700 font-normal text-right">Batch</th>
                {/* === Money cols (priority visible) — currency toggle === */}
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-20 text-emerald-700 font-normal text-right bg-emerald-50" title={`Thu từ user (ước tính Gói Vừa $0.0000053/credit, ~133đ/1K) — ${currency}`}>Thu ({currency})</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-20 text-red-700 font-normal text-right bg-red-50" title={`Chi phí Gemini API thực — ${currency}`}>Chi ({currency})</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-24 text-slate-700 font-normal text-right" title={`Lãi = Thu − Chi — margin % (${currency})`}>Lãi ({currency})</th>
                {/* === Token totals === */}
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-14 text-slate-700 font-normal text-right" title="Estimate total (pre-flight)">Est</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-14 text-slate-700 font-normal text-right" title="Real total = input + output (bundled)">Real</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-12 text-slate-700 font-normal text-right" title="(Real − Est) / Est">Δ%</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-28 text-slate-700 font-normal">User</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-[110px] text-slate-700 font-normal">Error</th>
                {/* === Scroll-right breakdown cols === */}
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-14 text-indigo-700 font-normal text-right bg-indigo-50" title="Input tokens (real)">Input</th>
                <th className="border-r border-slate-300 px-1.5 py-0.5 w-14 text-indigo-700 font-normal text-right bg-indigo-50" title="Visible output tokens = output_tokens − thoughts_tokens">Vis.Out</th>
                <th className="px-1.5 py-0.5 w-14 text-indigo-700 font-normal text-right bg-indigo-50" title="Thinking tokens (Gemini 2.5 thoughts, billed as output)">Think</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredJobs.map((job: JobRow, idx: number) => {
                const isErr = job.status === 'error' || job.status === 'stuck';
                const isRun = job.status === 'running';
                const isDone = job.status === 'done';
                const isQueued = job.status === 'queued';

                let rowBg = '';
                if (isErr) rowBg = 'bg-red-50';
                else if (isRun) rowBg = 'bg-blue-50';
                else if (isQueued) rowBg = 'bg-amber-50';
                else if (isDone) rowBg = 'bg-emerald-50';
                else rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'; // pending

                const isRetry = (job.file_name || '').startsWith('[RETRY] ');
                const displayName = isRetry ? (job.file_name || '').slice(8) : (job.file_name || '');

                const input = job.input_tokens || 0;
                const output = job.output_tokens || 0;           // bundled
                const thoughts = job.thoughts_tokens || 0;       // subset
                const visible = Math.max(0, output - thoughts);
                const realTotal = input + output;                // true billed total
                const estimate = job.estimated_tokens || 0;
                const delta = calculateDelta(estimate, realTotal);
                const money = calculateMoney(realTotal, job.cost_usd);
                const userProfile = profiles[job.user_id];
                const userRole = userProfile?.role || 'user';
                const isPaidUser = userRole === 'user';
                const roleBadge = userRole === 'admin'
                  ? { text: 'ADMIN', bg: 'bg-amber-200 text-amber-800' }
                  : userRole === 'internal'
                  ? { text: 'VDSC', bg: 'bg-violet-200 text-violet-800' }
                  : { text: 'PAID', bg: 'bg-emerald-200 text-emerald-800' };

                return (
                  <tr key={job.id} className={`${rowBg} hover:bg-slate-100/80 transition-colors`}>
                    <td className="px-1.5 py-1 font-mono text-slate-500 text-[10px]">{formatDate(job.created_at)}</td>
                    <td className="px-1.5 py-1 font-mono text-slate-500 text-[10px]">{formatTime(job.created_at)}</td>
                    <td className="px-1.5 py-1 whitespace-nowrap">{statusBadge(job.status)}</td>
                    <td className="px-1.5 py-1 text-center">
                      {isRetry && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-bold tracking-widest bg-orange-500 text-white">retry</span>
                      )}
                    </td>
                    <td className="px-1.5 py-1 truncate text-slate-800 font-medium" title={displayName}>{displayName}</td>
                    <td className="px-1.5 py-1 text-slate-600">{job.type}</td>
                    <td className="px-1.5 py-1 text-right text-slate-600 font-mono">{job.batch_index+1}/{job.total_batches}</td>
                    {/* === Money (priority) — only show for paid users === */}
                    <td className="px-1.5 py-1 text-right font-mono bg-emerald-50/40">
                      {isPaidUser
                        ? <span className="text-emerald-700 font-bold">{formatMoney(money.revenueUsd, currency)}</span>
                        : <span className="text-slate-300">—</span>
                      }
                    </td>
                    <td className="px-1.5 py-1 text-right font-mono text-red-700 bg-red-50/40">{formatMoney(money.costUsd, currency)}</td>
                    <td className="px-1.5 py-1 text-right font-mono whitespace-nowrap">
                      {isPaidUser ? (
                        <>
                          <span className={money.marginColorClass + ' font-bold'}>{formatMoney(money.profitUsd, currency)}</span>
                          <span className="text-slate-400 text-[9px] ml-1">({money.marginPct.toFixed(0)}%)</span>
                        </>
                      ) : (
                        <span className="text-violet-600 text-[9px] italic">free access</span>
                      )}
                    </td>
                    {/* === Token totals === */}
                    <td className="px-1.5 py-1 text-right text-slate-600 font-mono">{formatK(estimate)}</td>
                    <td className="px-1.5 py-1 text-right text-slate-600 font-mono font-bold">{formatK(realTotal)}</td>
                    <td className={`px-1.5 py-1 text-right font-mono ${delta.colorClass}`}>{delta.label}</td>
                    <td className="px-1.5 py-1 text-slate-700 text-[10px] truncate" title={`${userProfile?.name || ''} (${userProfile?.code || ''})\nrole: ${userRole}\n${job.user_id}`}>
                      <span className={`inline-block px-1 py-0 rounded-sm text-[8px] font-bold mr-1 ${roleBadge.bg}`}>{roleBadge.text}</span>
                      {userProfile?.name || userProfile?.code || job.user_id?.split('-')[0]}
                    </td>
                    <td className="px-1.5 py-1 text-red-600 text-[10px] font-mono italic truncate overflow-hidden max-w-[110px]" title={job.error || ''}>{job.error || ''}</td>
                    {/* === Breakdown tokens === */}
                    <td className="px-1.5 py-1 text-right font-mono text-indigo-700 bg-indigo-50/40">{formatK(input)}</td>
                    <td className="px-1.5 py-1 text-right font-mono text-indigo-700 bg-indigo-50/40">{formatK(visible)}</td>
                    <td className="px-1.5 py-1 text-right font-mono bg-indigo-50/40">
                      {thoughts > 0
                        ? <span className="text-indigo-700 font-bold">{formatK(thoughts)}</span>
                        : <span className="text-slate-300">—</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AllJobsTable;
